import React, { useEffect, useState } from 'react';
import { Autocomplete, TextField } from '@mui/material';
import { makeStyles } from '@mui/styles';

import { useTheme } from 'contexts/ThemeContext';

import { HelpTooltip, Text } from 'components/DataDisplay';
import Fade from 'components/Fade';
import { Item, Row } from 'components/Layout';

export const AsyncAutocomplete = ({
  multiple = false,
  disableCloseOnSelect = false,
  label,
  placeholder,
  options: propOptions, // <-- options passées directement
  fetchOptions, // <-- fonction fetch async (optionnelle)
  value = undefined,
  onChange,
  onChangeInput = () => {},
  ChipProps,
  renderOption,
  renderValue,
  debounceTime = 300,
  minimunChar = 2,
  color,
  bgColor,
  small,
  error,
  title,
  required,
  tooltip,
  exclude,
  formatLabel
}) => {
  const { panelColor, themeColors } = useTheme();
  const colorRules = themeColors[color] || color || themeColors[panelColor];

  const useStyles = makeStyles({
    root: {
      '& .MuiInputBase-root': {
        color: themeColors.light,
        backgroundColor: bgColor || 'transparent'
      },
      '& .MuiInputAdornment-root p': {
        color: themeColors.light
      },
      '& .MuiInput-underline:after': {
        borderBottomColor: colorRules
      },
      '& .MuiOutlinedInput-root': {
        '& fieldset': {
          borderColor: colorRules
        },
        '&:hover fieldset': {
          borderColor: themeColors.light
        },
        '&.Mui-focused fieldset': {
          borderColor: colorRules
        }
      }
    }
  });
  const classes = useStyles();

  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState(propOptions || []);
  const [loading, setLoading] = useState(false);

  // Sync options prop changes (pour mode statique)
  useEffect(() => {
    if (propOptions) {
      setOptions(propOptions);
    }
  }, [propOptions]);

  // Si fetchOptions est fourni, on fait l’async fetch au lieu d’utiliser propOptions
  useEffect(() => {
    // pas d’appel async si pas fourni
    if (!fetchOptions) {
      return;
    }

    if (!open || inputValue.length < minimunChar) {
      setOptions([]);
      return;
    }

    const controller = new AbortController();
    const handler = setTimeout(() => {
      setLoading(true);
      fetchOptions(inputValue)
        .then(results => {
          setOptions(results);
          setLoading(false);
        })
        .catch(err => {
          if (err.name !== 'AbortError') {
            console.error(err);
          }
          setLoading(false);
        });
    }, debounceTime);

    // eslint-disable-next-line consistent-return
    return () => {
      controller.abort();
      clearTimeout(handler);
    };
    // eslint-disable-next-line
  }, [inputValue, open, fetchOptions, debounceTime]);

  return (
    <Fade show>
      <Autocomplete
        multiple={multiple}
        disableCloseOnSelect={disableCloseOnSelect}
        ChipProps={ChipProps}
        className={classes.root}
        style={{ width: '100%' }}
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        value={value}
        onChange={(_, valueChange, reason) => {
          return onChange(valueChange, reason);
        }}
        inputValue={inputValue}
        onInputChange={(event, newInput) => {
          setInputValue(newInput);
          onChangeInput(event?.type !== 'click' ? newInput : '');
        }}
        options={
          typeof exclude === 'function'
            ? options.filter(f => !exclude(f))
            : options
        }
        loading={loading}
        filterOptions={x => x}
        renderOption={renderOption}
        getOptionLabel={formatLabel}
        renderTags={
          renderValue ? selected => <>{renderValue(selected)}</> : undefined
        }
        renderInput={params => (
          <>
            {title && (
              <Row spacing={0} justify="flex-start" alignItems="center">
                <Item justify="flex-start">
                  <Text noWrap color={error ? 'error' : null}>
                    {title}
                    {required && '*'}
                  </Text>
                  {tooltip && <HelpTooltip title={tooltip} />}
                </Item>
              </Row>
            )}{' '}
            <TextField
              {...params}
              error={error}
              label={label}
              placeholder={placeholder}
              variant="outlined"
              size={small ? 'small' : 'medium'}
              sx={{
                width: '100%',
                '& .MuiOutlinedInput-root': {
                  backgroundColor: `${bgColor || 'transparent'} !important` // ← Forcé ici
                },
                '& .MuiInputBase-input': {
                  backgroundColor: `${bgColor || 'transparent'} !important` // ← Et ici pour l'input direct
                }
              }}
            />
          </>
        )}
      />
    </Fade>
  );
};

export default AsyncAutocomplete;
